/**
 * Agent Readiness — pure checks + step ranker (ADR TODO, 2026-10-01).
 *
 * ONE source of truth for the dashboard's "Agent readiness" page. Pulls
 * together already-exposed server signals (crawler_access, ai_access,
 * sitemap, structured_data, agent_card, mcp, llms_txt + quality,
 * api_catalog, content_signals), per-site install coverage, the
 * visibility report (card fields + catalog eligibility), the raw
 * `site_compliance` values, and the latest site scan (suggested policy
 * URLs, suggested card fields, discrepancy flags, suggested skills).
 *
 * Rules (non-negotiable, see page spec):
 *   - DETERMINISTIC. No `Math.random()`, no clocks — ids come from
 *     closed enums (`check_id`, `step_id`), never free LLM text.
 *     Discrepancy ids are hashed from `(field + sources)` so they are
 *     stable across scans but still unique.
 *   - `unknown` is never a fix (ranker drops it). It renders as "—" in
 *     the UI, not as a passing check.
 *   - Store-only checks become `na` for websites (platform='website').
 *   - API catalog is `emerging` and OPTIONAL. It is NOT counted in the
 *     "N of M" fractions. llms.txt is `emerging` but counted (low weight
 *     in ordering).
 *   - Policies & Records always exposes two universal passing facts:
 *     "Signed records" (every agent action is signed and chained) and
 *     "Guardrails" (count surfaced when known; otherwise collapses to
 *     "— rules active").
 *
 * UI uses `buildReadinessChecks(inputs)` to render each section, and
 * `rankReadinessSteps(checks)` to pick the single "Your next step" tile
 * plus the ordered backlog behind the drawer.
 */
import { discrepancyFieldLabel } from './readiness-fixes.js';
/* ------------------------------------------------------------------ */
/* Pure helpers                                                        */
/* ------------------------------------------------------------------ */
/**
 * Stable 32-bit hash — FNV-1a over `input`. Deterministic (same input ->
 * same digits); good enough for a 7-char id suffix. We do NOT use it
 * for anything security-sensitive.
 */
export function hashReadinessId(input) {
    let h = 2166136261;
    for (let i = 0; i < input.length; i += 1) {
        h ^= input.charCodeAt(i);
        h = Math.imul(h, 16777619);
    }
    // Hex, lowercase, zero-padded to 8 chars; we return the first 7 so it
    // fits comfortably in a URL fragment / data-testid without being too
    // short to collide in practice.
    return (h >>> 0).toString(16).padStart(8, '0').slice(0, 7);
}
const ACTION_LABELS = {
    add_url: 'Add URL',
    use_value: 'Use value',
    review: 'Review',
    show_me_how: 'Show me how',
    turn_on: 'Turn on',
    edit_card: 'Edit card',
    edit_llms: 'Edit llms.txt',
};
function act(verb, labelOverride) {
    return { verb, label: labelOverride ?? ACTION_LABELS[verb] };
}
function tri(signal) {
    return signal ?? 'unknown';
}
/**
 * Reason-phrase for an UNKNOWN check — never a generic
 * "Not measured on this install." (2026-10-02 founder feedback). The
 * caller passes in the check id and the full `inputs` bundle; the
 * helper routes to the specific sentence that explains why the probe
 * couldn't answer.
 *
 * Precedence:
 *   1. First scan hasn't run — `inputs.signals === undefined` means we
 *      have no probe response at all. "Run your first check." applies
 *      to every check and is the broadest explanation.
 *   2. The probe ran but the specific signal is `unknown` because the
 *      probe depends on the server-side install (Worker/plugin). The
 *      edge-side agent-card + MCP + llms.txt probes read what the
 *      merchant's domain actually serves at the public endpoint, so
 *      a `unknown` here is almost always "needs the Worker or the
 *      WordPress plugin".
 *   3. Homepage / crawler reachability probes depend on a reachable
 *      homepage ("We couldn't reach your homepage, so this wasn't
 *      checked.").
 *   4. Catch-all — "This check wasn't included in your most recent
 *      scan." (never a leading "not measured").
 */
function unknownReason(check_id, inputs) {
    if (inputs.signals === undefined)
        return 'Run your first check.';
    // Server-side signals the Worker/plugin owns (ADR-0211, ADR-0219).
    if (check_id === 'agent_card'
        || check_id === 'mcp'
        || check_id === 'llms_txt'
        || check_id === 'api_catalog'
        // ADR-0223 — both `ard` and `ucp` sit at `/.well-known/*` and
        // depend on the Worker / plugin to serve them from the merchant's
        // origin. (ARD is additionally forced to `present` whenever Sill
        // is installed — the signed catalog is published at the edge —
        // so this branch is reached only in the rare case where Sill is
        // installed but the signal still read `unknown`; the message
        // still accurately describes the server-side owner.)
        || check_id === 'ard'
        || check_id === 'ucp') {
        return 'Needs the Cloudflare Worker or WordPress plugin.';
    }
    // Robots / sitemap / structured_data / content_signals / crawler
    // access all require the public homepage to answer. When the
    // homepage probe itself failed we report that explicitly.
    if (check_id === 'crawler.access') {
        return "We couldn't reach your homepage, so this wasn't checked.";
    }
    if (check_id === 'robots.ai_access'
        || check_id === 'robots.txt'
        || check_id === 'sitemap'
        || check_id === 'structured_data'
        || check_id === 'content_signals') {
        return 'This check was not included in your most recent scan.';
    }
    return 'This check was not included in your most recent scan.';
}
/**
 * Canonical store-skill ids (the seven commerce defaults).
 *
 * A WEBSITE (non-store) must not be told to turn on these — "turn on
 * `browse_catalog`" is nonsense advice for a SaaS marketing site.
 * Callers that render scan suggestions on a website filter these out
 * before showing them to the merchant.
 */
export const STORE_SKILL_IDS = new Set([
    'browse_catalog',
    'check_availability',
    'place_order',
    'order_status',
    'track_shipment',
    'request_refund',
    'recommend',
]);
/** True iff `skill_id` is one of the seven canonical store skills. */
export function isStoreSkillId(skill_id) {
    return STORE_SKILL_IDS.has(skill_id);
}
/** The seven known assistant/search bots that materially gate reach. */
const MAJOR_BOT_IDS = [
    'GPTBot',
    'ChatGPT-User',
    'ClaudeBot',
    'PerplexityBot',
    'Google-Extended',
    'Googlebot',
    'Bingbot',
];
/** Field-id → merchant-readable label for discrepancy headlines. */
const DISCREPANCY_FIELD_LABEL = {
    shipping_zones: 'Shipping zones',
    return_window_days: 'Return window',
    returns_policy: 'Returns policy',
    currency: 'Currency',
    price_range: 'Price range',
    subscription_billing: 'Subscription cadence',
};
const CARD_FIELD_LABEL = {
    name: 'Business name',
    description: 'Description',
    price_range: 'Price range',
    currency: 'Currency',
    shipping_zones: 'Shipping zones',
    return_window_days: 'Return window',
    subscription_billing: 'Subscription cadence',
    agent_card_authoritative: 'Authoritative agent card',
};
/** Returns true when the merchant's own domain already serves a given policy. */
function hasPolicyUrl(url) {
    return typeof url === 'string' && url.trim().length > 0;
}
/* ------------------------------------------------------------------ */
/* buildReadinessChecks                                                */
/* ------------------------------------------------------------------ */
/**
 * ADR-0225 — check ids that are purely informational: rendered as an
 * uncounted row in their section, but NEVER listed in "What's left"
 * (they are not something to do) and never ranked as a step.
 */
export const INFORMATIONAL_CHECK_IDS = new Set(['x402', 'pap']);
/**
 * Build the ordered list of readiness checks for the dashboard page.
 *
 * The caller renders:
 *   - `.filter((c) => c.group === 'discoverable')` as the Discoverable section
 *   - `.filter((c) => c.group === 'policies')`     as the Policies & records section
 * Section fractions:
 *   - counted / passing come from {@link tallyReadinessSection}.
 */
export function buildReadinessChecks(inputs) {
    const checks = [];
    const s = inputs.signals ?? {};
    const is_store = inputs.platform_kind === 'store';
    // -------- Discoverable --------
    // 1. Crawler access (WAF/firewall reachability).
    {
        const v = s.crawler_access ?? 'unknown';
        const state = v === 'open' ? 'pass' : v === 'blocked' ? 'fail' : 'unknown';
        const fail = state === 'fail';
        checks.push({
            check_id: 'crawler.access',
            group: 'discoverable',
            state,
            severity: 'block',
            emerging: false,
            uncounted: false,
            headline: fail ? 'AI crawlers get 403 Forbidden.' : 'Crawlers can reach your pages.',
            evidence: fail
                ? 'Your homepage answered our check with 403. Bot protection at your host or firewall is the usual cause.'
                : state === 'pass'
                    ? 'Homepage answered 200 to a generic crawler.'
                    : unknownReason('crawler.access', inputs),
            action: fail ? act('show_me_how') : null,
            step_id: fail ? 'crawler.blocked' : null,
        });
    }
    // 2. Robots.txt — the file + whether a major bot is blocked.
    {
        const robots_present = tri(s.robots_txt);
        if (robots_present === 'absent') {
            checks.push({
                check_id: 'robots.missing',
                group: 'discoverable',
                state: 'fail',
                severity: 'warn',
                emerging: false,
                uncounted: false,
                headline: 'No robots.txt file found.',
                evidence: 'robots.txt tells search and AI bots what they may fetch; without one, each bot picks its own default.',
                action: act('show_me_how'),
                step_id: 'robots.missing',
            });
        }
        else {
            const ai_access = s.ai_access ?? 'unknown';
            const pass = robots_present === 'present' && (ai_access === 'open' || ai_access === 'unknown');
            checks.push({
                check_id: 'robots.ai_access',
                group: 'discoverable',
                state: ai_access === 'blocked' ? 'fail'
                    : ai_access === 'partial' ? 'fail'
                        : robots_present === 'present' ? 'pass'
                            : 'unknown',
                severity: ai_access === 'blocked' ? 'block' : 'warn',
                emerging: false,
                uncounted: false,
                headline: ai_access === 'blocked'
                    ? 'robots.txt blocks AI bots.'
                    : ai_access === 'partial'
                        ? 'robots.txt blocks at least one AI bot.'
                        : pass
                            ? 'robots.txt lets AI bots in.'
                            : 'robots.txt was not checked yet.',
                evidence: ai_access === 'blocked'
                    ? 'We checked 8 AI and search bots. All are disallowed by the general rule or a specific rule.'
                    : ai_access === 'partial'
                        ? 'We checked 8 AI and search bots. At least one is disallowed.'
                        : pass
                            ? 'We checked 8 AI and search bots.'
                            : unknownReason('robots.txt', inputs),
                action: ai_access === 'blocked' || ai_access === 'partial' ? act('show_me_how') : null,
                step_id: ai_access === 'blocked'
                    ? 'robots.bot_blocked:all'
                    : ai_access === 'partial'
                        ? 'robots.bot_blocked:partial'
                        : null,
            });
        }
    }
    // 3. Sitemap.
    {
        const v = tri(s.sitemap);
        checks.push({
            check_id: 'sitemap',
            group: 'discoverable',
            state: v === 'present' ? 'pass' : v === 'absent' ? 'fail' : 'unknown',
            severity: 'warn',
            emerging: false,
            uncounted: false,
            headline: v === 'absent' ? 'No sitemap found.' : 'Sitemap found.',
            evidence: v === 'present' ? 'Lists your public pages so crawlers find them.'
                : v === 'absent' ? 'A sitemap at /sitemap.xml helps crawlers find every public page.'
                    : unknownReason('sitemap', inputs),
            action: v === 'absent' ? act('show_me_how') : null,
            step_id: v === 'absent' ? 'sitemap.missing' : null,
        });
    }
    // 4. Structured data (JSON-LD for Product / Offer / Organization).
    // Store-only — a plain content website doesn't need Product markup.
    if (is_store) {
        const v = tri(s.structured_data);
        checks.push({
            check_id: 'structured_data',
            group: 'discoverable',
            state: v === 'present' ? 'pass' : v === 'absent' ? 'fail' : 'unknown',
            severity: 'warn',
            emerging: false,
            uncounted: false,
            headline: v === 'present'
                ? 'Product data found on your pages.'
                : v === 'absent'
                    ? 'No product markup on your pages.'
                    : 'Product markup was not measured.',
            evidence: v === 'present'
                ? 'Product, Offer and Organization structured data detected.'
                : v === 'absent'
                    ? 'Agents fall back to scraping prose when Product and Offer markup are missing.'
                    : unknownReason('structured_data', inputs),
            action: v === 'absent' ? act('show_me_how') : null,
            step_id: v === 'absent' ? 'structured_data.missing' : null,
        });
    }
    // 5. Install coverage — the single highest-priority install step.
    if (inputs.install) {
        const action = inputs.install.action;
        const live = inputs.install.browser_live || inputs.install.server_side_live;
        checks.push({
            check_id: 'install.coverage',
            group: 'discoverable',
            state: action === null ? 'pass' : 'fail',
            severity: 'block',
            emerging: false,
            uncounted: false,
            headline: action === null
                ? 'Sill is installed on your site.'
                : installActionHeadline(action),
            evidence: action === null
                ? live ? 'Browser or server-side layer is live.' : 'Discovery files are served from your domain.'
                : installActionEvidence(action),
            action: action !== null ? act('show_me_how') : null,
            step_id: action !== null ? `install.${action.kind}` : null,
        });
    }
    // 6. Agent card — presence + per-field completeness.
    {
        const v = tri(s.agent_card);
        const card_fields_total = (inputs.visibility?.card_fields_present.length ?? 0)
            + (inputs.visibility?.card_fields_missing.length ?? 0);
        const card_fields_filled = inputs.visibility?.card_fields_present.length ?? 0;
        const present = v === 'present';
        checks.push({
            check_id: 'agent_card',
            group: 'discoverable',
            state: present ? 'pass' : v === 'absent' ? 'fail' : 'unknown',
            severity: 'warn',
            emerging: false,
            uncounted: false,
            headline: present
                ? 'Agent card published and signed.'
                : v === 'absent'
                    ? 'Agent card not served from your domain.'
                    : 'Agent card was not measured.',
            evidence: present
                ? card_fields_total > 0
                    ? `${card_fields_filled} of ${card_fields_total} details filled in.`
                    : 'Served from your domain, signature verifies.'
                : v === 'absent'
                    ? 'Agents cannot find a Sill-signed card at /.well-known/agent-card.json.'
                    : unknownReason('agent_card', inputs),
            action: present ? act('edit_card') : v === 'absent' ? act('show_me_how') : null,
            step_id: v === 'absent' ? 'agent_card.missing'
                : null,
        });
    }
    // 7. MCP server.
    {
        const v = tri(s.mcp);
        const live_count = inputs.live_skill_count;
        // MCP reachable but zero skills live: for a STORE this is a
        // warn-severity fail — the MCP endpoint can accept a connection
        // but there is nothing for agents to call, so turning on a store
        // skill is a real fix. For a WEBSITE (SaaS marketing, blog, docs)
        // the seven canonical store skills don't apply at all — the right
        // fix is "add a custom skill", not "turn on store skills". We
        // keep the row visible so the merchant sees the shape of their
        // MCP surface, but mark it informational (`na`) and point the
        // action copy at Settings → Skills (custom skill).
        const no_tools = v === 'present' && typeof live_count === 'number' && live_count === 0;
        const tool_word = typeof live_count === 'number'
            ? `${live_count} ${live_count === 1 ? 'skill' : 'skills'} live.`
            : null;
        if (no_tools && !is_store) {
            checks.push({
                check_id: 'mcp',
                group: 'discoverable',
                state: 'na',
                severity: 'warn',
                emerging: false,
                // `na` is informational; keep it out of the counted fraction so
                // a website's score is not dragged down by a store-only check.
                uncounted: true,
                headline: 'No MCP tools yet.',
                evidence: "Store skills don't apply to a website. Add a custom skill (for example \"Book a demo\" or \"Start a trial\") so agents have something to call.",
                action: act('turn_on', 'Add a custom skill'),
                step_id: 'mcp.no_tools.website',
            });
        }
        else {
            checks.push({
                check_id: 'mcp',
                group: 'discoverable',
                state: no_tools ? 'fail'
                    : v === 'present' ? 'pass'
                        : v === 'absent' ? 'fail'
                            : 'unknown',
                severity: 'warn',
                emerging: false,
                uncounted: false,
                headline: no_tools ? 'Your MCP server has no tools.'
                    : v === 'present' ? 'MCP server live.'
                        : v === 'absent' ? 'MCP server not reachable.'
                            : 'MCP server was not measured.',
                evidence: no_tools ? 'Agents can connect, but no skills are turned on, so there is nothing to call.'
                    : v === 'present' ? (tool_word !== null ? `${tool_word} Offered to AI clients as MCP tools.` : 'Skills are offered to AI clients as MCP tools.')
                        : v === 'absent' ? 'Agents using Model Context Protocol will not see your skills.'
                            : unknownReason('mcp', inputs),
                action: no_tools ? act('turn_on', 'Turn on skills')
                    : v === 'absent' ? act('show_me_how')
                        : null,
                step_id: no_tools ? 'mcp.no_tools'
                    : v === 'absent' ? 'mcp.missing'
                        : null,
            });
        }
    }
    // ADR-0222 — Business skills group suggestion. Only fires on non-
    // store sites. Pass-through on stores (the row is simply absent).
    //
    // Three-condition gate:
    //   (a) platform_kind === 'website',
    //   (b) the latest scan detected ≥1 business link (pricing / demo /
    //       signup / booking),
    //   (c) the merchant has saved zero business skills so far.
    //
    // Severity: `warn`, uncounted (same posture as `mcp.no_tools.website`)
    // — informational, not score-dragging.
    if (!is_store
        && (inputs.scan?.detected_business_links?.length ?? 0) > 0
        && inputs.has_published_business_link !== true) {
        const detected = inputs.scan.detected_business_links;
        const summary = detected
            .map((d) => `${d.kind}: ${d.url}`)
            .slice(0, 4)
            .join('\n');
        checks.push({
            check_id: 'business_links',
            group: 'discoverable',
            state: 'fail',
            severity: 'warn',
            emerging: false,
            uncounted: true,
            headline: 'Let agents send people to your pricing, demo, sign-up and booking pages.',
            evidence: `We found ${detected.length} link${detected.length === 1 ? '' : 's'} on your homepage you can hand to agents in one click.\n${summary}`,
            action: act('turn_on', 'Open skills'),
            step_id: 'business_links.unpublished',
        });
    }
    // 8. llms.txt — presence + quality. Emerging; counted but low weight.
    {
        const v = tri(s.llms_txt);
        const quality = inputs.llms_quality ?? 'unknown';
        const pass = v === 'present' && quality !== 'thin';
        const fail = v === 'absent' || quality === 'thin';
        checks.push({
            check_id: 'llms_txt',
            group: 'discoverable',
            state: pass ? 'pass' : fail ? 'fail' : 'unknown',
            severity: 'warn',
            emerging: true,
            uncounted: false,
            headline: pass ? 'llms.txt present with a description.'
                : v === 'absent' ? 'No llms.txt served from your domain.'
                    : quality === 'thin' ? 'llms.txt is present but thin.'
                        : 'llms.txt was not measured.',
            evidence: pass ? 'Emerging standard, low weight.'
                : v === 'absent' ? 'A short llms.txt tells assistants what the site is about.'
                    : quality === 'thin' ? 'The current file has few descriptive lines; add product and policy anchors.'
                        : unknownReason('llms_txt', inputs),
            action: pass ? act('edit_llms') : fail ? act('edit_llms') : null,
            step_id: fail ? 'llms_txt.missing' : null,
        });
    }
    // 9. API catalog — OPTIONAL and NOT COUNTED. On a platform that
    // can't serve `/.well-known/*` (Shopify) we classify this check as
    // `na` with evidence that points to the signed catalog Sill
    // publishes at the edge, so the merchant isn't told "add a file"
    // for an artifact their platform won't let apps serve (ADR-0221).
    {
        const v = tri(s.api_catalog);
        const constraints = inputs.platform_constraints;
        const well_known_impossible = constraints !== undefined && constraints.serves_well_known === false;
        if (well_known_impossible && v !== 'present') {
            const edge_url = constraints.edge_catalog_url ?? '';
            checks.push({
                check_id: 'api_catalog',
                group: 'discoverable',
                state: 'na',
                severity: 'warn',
                emerging: true,
                uncounted: true,
                headline: 'API catalog served from the Sill edge.',
                evidence: edge_url.length > 0
                    ? `Shopify doesn't allow apps to serve /.well-known files. Your signed ARD catalog is published at ${edge_url} and linked from your agent card.`
                    : "Shopify doesn't allow apps to serve /.well-known files. Your signed ARD catalog is published at the Sill edge and linked from your agent card.",
                action: null,
                step_id: null,
            });
        }
        else {
            checks.push({
                check_id: 'api_catalog',
                group: 'discoverable',
                state: v === 'present' ? 'pass' : v === 'absent' ? 'na' : 'unknown',
                severity: 'warn',
                emerging: true,
                uncounted: true,
                headline: v === 'present' ? 'API catalog served.'
                    : 'API catalog not set.',
                evidence: v === 'present' ? 'Optional, emerging standard.'
                    : v === 'absent' ? 'Optional, emerging standard. Not counted.'
                        : unknownReason('api_catalog', inputs),
                action: null,
                step_id: null,
            });
        }
    }
    // 10. ARD catalog (ADR-0223) — OPTIONAL and NOT COUNTED. Mirrors the
    // api_catalog platform-constraints branch: on Shopify (serves_well_known
    // = false) we render `na` with evidence pointing at the signed catalog
    // Sill publishes at the edge.
    {
        const v = tri(s.ard);
        const constraints = inputs.platform_constraints;
        const well_known_impossible = constraints !== undefined && constraints.serves_well_known === false;
        if (well_known_impossible && v !== 'present') {
            const edge_url = constraints.edge_catalog_url ?? '';
            checks.push({
                check_id: 'ard',
                group: 'discoverable',
                state: 'na',
                severity: 'warn',
                emerging: true,
                uncounted: true,
                headline: 'ARD catalog served from the Sill edge.',
                evidence: edge_url.length > 0
                    ? `Shopify doesn't allow apps to serve /.well-known files. Your signed ARD catalog is published at ${edge_url} and linked from your agent card.`
                    : "Shopify doesn't allow apps to serve /.well-known files. Your signed ARD catalog is published at the Sill edge and linked from your agent card.",
                action: null,
                step_id: null,
            });
        }
        else {
            checks.push({
                check_id: 'ard',
                group: 'discoverable',
                state: v === 'present' ? 'pass' : v === 'absent' ? 'fail' : 'unknown',
                severity: 'warn',
                emerging: true,
                uncounted: true,
                headline: v === 'present' ? 'ARD catalog served.'
                    : v === 'absent' ? 'ARD catalog not set.'
                        : 'ARD catalog was not measured.',
                evidence: v === 'present' ? 'A machine-readable index pointing agents at your capabilities.'
                    : v === 'absent' ? 'Emerging standard, not counted. Few agents read it today. Installing Sill publishes a signed ARD catalog.'
                        : unknownReason('ard', inputs),
                action: v === 'absent' ? act('show_me_how') : null,
                step_id: v === 'absent' ? 'ard.missing' : null,
            });
        }
    }
    // 11. UCP business profile (ADR-0223) — STORE-ONLY and COUNTED (warn
    // severity, never block). Websites render as `na` with reason "Only
    // applies to stores." Shopify stores serve `/.well-known/ucp` natively,
    // so the probe passes without any Sill install — no Shopify-specific
    // branch here, unlike api_catalog / ard.
    {
        const v = tri(s.ucp);
        if (!is_store) {
            checks.push({
                check_id: 'ucp',
                group: 'discoverable',
                state: 'na',
                severity: 'warn',
                emerging: false,
                uncounted: true,
                headline: 'UCP profile',
                evidence: 'Only applies to stores.',
                action: null,
                step_id: null,
            });
        }
        else {
            checks.push({
                check_id: 'ucp',
                group: 'discoverable',
                state: v === 'present' ? 'pass' : v === 'absent' ? 'fail' : 'unknown',
                severity: 'warn',
                emerging: false,
                uncounted: false,
                headline: v === 'present' ? 'UCP profile served.'
                    : v === 'absent' ? 'UCP profile not served.'
                        : 'UCP profile was not measured.',
                evidence: v === 'present' ? 'The business profile Google and other platforms read to let agents transact.'
                    : v === 'absent' ? 'Universal Commerce Protocol lets agents like Google read your business shape. Sill publishes a UCP profile when installed.'
                        : unknownReason('ucp', inputs),
                action: v === 'absent' ? act('show_me_how') : null,
                step_id: v === 'absent' ? 'ucp.missing' : null,
            });
        }
    }
    // 12. x402 — informational "charges agents". ADR-0225: NEVER counted,
    // never a fail, never a fix. Rendered as an uncounted info row in the
    // Discoverable section next to the other protocol signals, with a
    // neutral tone when absent. No `step_id` → never surfaces in the Fix
    // ranker; no FixDrawer branch.
    {
        const v = tri(s.x402);
        const kind = s.x402_kind;
        if (v === 'present') {
            checks.push({
                check_id: 'x402',
                group: 'discoverable',
                // Informational: `na` renders the neutral "–" marker (never the
                // ✓ pass tick) and hides the Evidence link.
                state: 'na',
                severity: 'warn',
                emerging: true,
                // Never scored; keep out of the counted fraction so a merchant
                // that charges agents sees it as an uncounted info row.
                uncounted: true,
                headline: kind === 'pay_per_crawl'
                    ? 'Charges AI crawlers (Cloudflare pay-per-crawl).'
                    : 'Charges agents per request (x402).',
                evidence: kind === 'pay_per_crawl'
                    ? 'Your homepage answered our check with HTTP 402 and a Cloudflare crawler-price header. Informational only. Not counted.'
                    : 'Your homepage answered our check with HTTP 402 and x402 payment requirements. Informational only. Not counted.',
                action: null,
                step_id: null,
            });
        }
        else {
            // Absent + unknown both render as a neutral `na` row.
            checks.push({
                check_id: 'x402',
                group: 'discoverable',
                state: 'na',
                severity: 'warn',
                emerging: true,
                uncounted: true,
                headline: v === 'absent' ? "Doesn't charge agents." : 'No paid access detected.',
                evidence: 'Informational only. Not counted.',
                action: null,
                step_id: null,
            });
        }
    }
    // 13. PAP — informational row (ADR-0252). Live today: Sill publishes
    // /.well-known/poppy.json on Cloudflare Worker and WordPress plugin
    // (1.14+) installs. Kept uncounted so adding this row does not shift
    // every site's score; copy for missing points at how Sill will serve
    // it on install.
    {
        const v = tri(s.pap);
        checks.push({
            check_id: 'pap',
            group: 'discoverable',
            state: 'na',
            severity: 'warn',
            emerging: true,
            uncounted: true,
            headline: v === 'present' ? 'Personal Agent Protocol file published.'
                : v === 'absent' ? 'No Personal Agent Protocol file served yet.'
                    : 'Personal Agent Protocol file was not measured.',
            evidence: v === 'present'
                ? 'Your site serves /.well-known/poppy.json. A shopper’s personal agent reads this to open a guest session and use your skills. Informational only. Not counted.'
                : v === 'absent'
                    ? 'A guest-session file for shoppers’ personal agents. Sill publishes it for you with the Cloudflare Worker or the WordPress plugin (1.14+). Informational only. Not counted.'
                    : 'A guest-session file for shoppers’ personal agents. Sill publishes it for you with the Cloudflare Worker or the WordPress plugin (1.14+). Informational only. Not counted.',
            action: null,
            step_id: null,
        });
    }
    // -------- Policies & records --------
    // ToS / Privacy / Returns URL rows.
    const compliance = inputs.site_compliance ?? null;
    const suggested_urls = inputs.scan?.suggested_policy_urls ?? [];
    function suggestedUrlFor(kinds) {
        for (const k of kinds) {
            const m = suggested_urls.find((u) => u.kind === k);
            if (m)
                return m;
        }
        return null;
    }
    // ToS
    {
        const url = compliance?.seller_tos_url ?? null;
        const has = hasPolicyUrl(url);
        const suggested = !has ? suggestedUrlFor(['seller_tos_url', 'terms', 'tos']) : null;
        checks.push({
            check_id: 'policy.tos',
            group: 'policies',
            state: has ? 'pass' : compliance === null ? 'unknown' : 'fail',
            severity: 'warn',
            emerging: false,
            uncounted: false,
            headline: has
                ? 'Terms of service linked.'
                : suggested !== null ? 'Your terms of service link isn\'t set.' : 'No terms of service page found.',
            evidence: has
                ? (url ?? '')
                : suggested !== null
                    ? `We found it at ${suggested.url}. Save it so agents can read it.`
                    : 'Agents look for returns, privacy and terms before they buy.',
            action: has ? null : suggested !== null ? act('add_url', 'Add URL') : act('add_url'),
            step_id: has ? null : 'policy.tos_missing',
        });
    }
    // Privacy
    {
        const url = compliance?.seller_privacy_policy_url ?? null;
        const has = hasPolicyUrl(url);
        const suggested = !has ? suggestedUrlFor(['seller_privacy_policy_url', 'privacy', 'privacy_policy']) : null;
        checks.push({
            check_id: 'policy.privacy',
            group: 'policies',
            state: has ? 'pass' : compliance === null ? 'unknown' : 'fail',
            severity: 'warn',
            emerging: false,
            uncounted: false,
            headline: has
                ? 'Privacy policy linked.'
                : suggested !== null ? 'Your privacy policy link isn\'t set.' : 'No privacy policy page found.',
            evidence: has
                ? (url ?? '')
                : suggested !== null
                    ? `We found it at ${suggested.url}. Save it so agents can read it.`
                    : 'Agents expect to read how buyer data is handled.',
            action: has ? null : act('add_url'),
            step_id: has ? null : 'policy.privacy_missing',
        });
    }
    // Returns (store only — a content website doesn't need one)
    if (is_store) {
        const text = compliance?.returns_policy ?? null;
        const has = hasPolicyUrl(text);
        const suggested = !has ? suggestedUrlFor(['refund_policy', 'return_policy', 'returns']) : null;
        checks.push({
            check_id: 'policy.returns',
            group: 'policies',
            state: has ? 'pass' : compliance === null ? 'unknown' : 'fail',
            severity: 'warn',
            emerging: false,
            uncounted: false,
            headline: has
                ? 'Returns policy linked.'
                : suggested !== null ? 'Your returns policy link isn\'t set.' : 'No returns policy page found.',
            evidence: has
                ? 'Linked from your agent card.'
                : suggested !== null
                    ? `We found it at ${suggested.url}. Save it so agents can read it.`
                    : 'Agents check returns before placing paid orders.',
            action: has ? null : act('add_url'),
            step_id: has ? null : 'policy.returns_missing',
        });
    }
    // Discrepancy flags — one row per flagged field. Hashed id so it stays
    // stable across scans.
    for (const flag of inputs.scan?.discrepancy_flags ?? []) {
        const id = `scan.discrepancy:${hashReadinessId([flag.field, ...flag.sources].join('|'))}`;
        // Use the shared `discrepancyFieldLabel` (in `readiness-fixes.ts`)
        // so free-text pt_v4 ids like `skills/commerce_applicability`
        // humanize correctly instead of leaking the raw path into the
        // merchant-facing headline. The direct `DISCREPANCY_FIELD_LABEL`
        // lookup is still used first for the ids this file owns.
        const label = DISCREPANCY_FIELD_LABEL[flag.field]
            ?? discrepancyFieldLabel(flag.field);
        checks.push({
            check_id: id,
            group: 'policies',
            state: 'fail',
            severity: 'warn',
            emerging: false,
            uncounted: false,
            headline: `Your agent card and site disagree on ${label.toLowerCase()}.`,
            evidence: flag.description,
            action: act('review'),
            step_id: id,
        });
    }
    // Suggested card fields — one row for each missing field that the scan
    // produced a suggestion for. These are policy-shaped (return window,
    // shipping zones, currency, subscription billing).
    const seenCardFields = new Set();
    for (const sug of inputs.scan?.suggested_card_profile_fields ?? []) {
        const field = sug.field_id;
        const present = inputs.visibility?.card_fields_present.includes(field) ?? false;
        if (present)
            continue;
        seenCardFields.add(field);
        const label = CARD_FIELD_LABEL[field] ?? field;
        const value = sug.value.trim();
        const withUnit = field === 'return_window_days' && /^\d+$/.test(value)
            ? `${value} ${value === '1' ? 'day' : 'days'}`
            : value;
        const shortValue = withUnit.length > 40 ? withUnit.slice(0, 40).trim() + '…' : withUnit;
        checks.push({
            check_id: `card.field:${field}`,
            group: 'policies',
            state: 'fail',
            severity: 'warn',
            emerging: false,
            uncounted: false,
            headline: `Your agent card has no ${label.toLowerCase()}.`,
            evidence: value.length > 0
                ? `We read ${shortValue} from your site.`
                : 'Your site has a value we can read for this field.',
            action: value.length > 0 ? act('use_value', `Use ${shortValue}`) : act('review'),
            step_id: `card.field:${field}`,
        });
    }
    // Return window — emit a passing row when the field is set on the
    // card so the UI can render the inline edit (§E). The row isn't
    // counted toward the fraction (merchant-authoritative; no probe
    // measures it) and only appears for stores.
    if (is_store) {
        const present = inputs.visibility?.card_fields_present.includes('return_window_days') ?? false;
        if (present && !seenCardFields.has('return_window_days')) {
            checks.push({
                check_id: 'card.field:return_window_days',
                group: 'policies',
                state: 'pass',
                severity: 'warn',
                emerging: false,
                uncounted: true,
                headline: 'Return window set.',
                evidence: '',
                action: null,
                step_id: null,
            });
        }
    }
    // Universal passing facts for every active site.
    const rule_count = inputs.active_rule_count;
    checks.push({
        check_id: 'guardrails.active',
        group: 'policies',
        // No active rules means agents act without limits: say so. Unknown
        // count (policy not loaded) is not a fix.
        state: rule_count === 0 ? 'fail' : typeof rule_count === 'number' ? 'pass' : 'unknown',
        severity: 'warn',
        emerging: false,
        uncounted: false,
        headline: rule_count === 0 ? 'No guardrails are active.' : 'Guardrails',
        evidence: rule_count === 0
            ? 'Agents can act without limits on amount, quantity or refunds. Turn on the defaults in Guardrails.'
            : typeof rule_count === 'number'
                ? `${rule_count.toLocaleString('en-US')} ${rule_count === 1 ? 'rule' : 'rules'} active.`
                : 'Couldn\'t check. Open Guardrails to review.',
        action: rule_count === 0 ? act('show_me_how') : null,
        step_id: rule_count === 0 ? 'guardrails.none' : null,
    });
    checks.push({
        check_id: 'audit.signed_records',
        group: 'policies',
        state: 'pass',
        severity: 'warn',
        emerging: false,
        uncounted: false,
        headline: 'Signed records',
        evidence: inputs.has_signed_records === false
            ? 'Every agent action will be signed and chained. Export any time.'
            : 'Every agent action is signed and chained. Export any time.',
        action: null,
        step_id: null,
    });
    // Content signals (robots.txt declarations: AI usage, training).
    // Pass when 'present', fail when 'absent' — low priority, not shown
    // until policies are in place.
    {
        const v = tri(s.content_signals);
        checks.push({
            check_id: 'content_signals',
            group: 'policies',
            state: v === 'present' ? 'pass' : v === 'absent' ? 'fail' : 'unknown',
            severity: 'warn',
            emerging: true,
            uncounted: false,
            headline: v === 'present' ? 'AI usage declared in robots.txt.'
                : v === 'absent' ? 'No AI content signals declared.'
                    : 'AI content signals were not measured.',
            evidence: v === 'present' ? 'Your robots.txt declares content signals for AI use.'
                : v === 'absent' ? 'Robots.txt does not state your AI-content preferences.'
                    : unknownReason('content_signals', inputs),
            action: v === 'absent' ? act('show_me_how') : null,
            step_id: v === 'absent' ? 'content_signals.missing' : null,
        });
    }
    return checks;
}
/** The counted / passing tally for a section. Uncounted + `na` + `unknown` are excluded. */
export function tallyReadinessSection(checks, group) {
    let counted = 0;
    let passing = 0;
    for (const c of checks) {
        if (c.group !== group)
            continue;
        if (c.uncounted)
            continue;
        if (c.state === 'na' || c.state === 'unknown')
            continue;
        counted += 1;
        if (c.state === 'pass')
            passing += 1;
    }
    return { group, counted, passing };
}
/* ------------------------------------------------------------------ */
/* rankReadinessSteps                                                  */
/* ------------------------------------------------------------------ */
const STEP_PRIORITY = {
    // Blockers — nothing agents can do until these are fixed.
    'crawler.blocked': 0,
    'robots.bot_blocked:all': 1,
    'robots.bot_blocked:partial': 2,
    // Install actions — fail-closed for the whole protocol surface.
    'install.deploy_worker': 10,
    'install.install_plugin': 11,
    'install.add_cloudflare': 12,
    'install.paste_embed': 13,
    'install.add_redirect': 14,
    'install.connect_store': 15,
    // Discoverability gaps.
    'robots.missing': 20,
    'sitemap.missing': 21,
    'agent_card.missing': 22,
    'structured_data.missing': 23,
    'mcp.missing': 24,
    'mcp.no_tools': 24,
    // Policies.
    'policy.tos_missing': 30,
    'policy.privacy_missing': 31,
    'policy.returns_missing': 32,
    // Content signals.
    'content_signals.missing': 40,
    // UCP — store-only; counted but low priority (sits above emerging).
    'ucp.missing': 45,
    // Emerging.
    'llms_txt.missing': 50,
    'ard.missing': 55,
};
/** Priority for a step id — unknown ids rank LAST (999). */
function stepPriority(step_id) {
    if (step_id.startsWith('scan.discrepancy:'))
        return 25;
    if (step_id.startsWith('card.field:'))
        return 35;
    if (step_id.startsWith('skill.suggested:'))
        return 45;
    // Install actions share a per-kind priority: look up the base kind.
    if (step_id.startsWith('install.')) {
        return STEP_PRIORITY[step_id] ?? 15;
    }
    return STEP_PRIORITY[step_id] ?? 999;
}
/**
 * Rank the ordered list of fixable steps. `unknown` is never a fix.
 * Store-only checks that collapsed to `na` are not in the input.
 * Emerging checks (llms.txt, api_catalog) rank last.
 *
 * Ordering is stable: within the same priority bucket, we fall back to
 * the input index so repeated calls on the same input yield the same
 * step list byte-for-byte.
 */
export function rankReadinessSteps(checks) {
    const indexed = [];
    for (let i = 0; i < checks.length; i += 1) {
        const c = checks[i];
        if (c.state !== 'fail')
            continue;
        if (c.step_id === null)
            continue;
        const prio = (c.emerging ? 100 : 0) + stepPriority(c.step_id);
        indexed.push({
            i,
            prio,
            step: {
                step_id: c.step_id,
                check_id: c.check_id,
                group: c.group,
                severity: c.severity,
                emerging: c.emerging,
                headline: c.headline,
                evidence: c.evidence,
                action: c.action,
            },
        });
    }
    indexed.sort((a, b) => a.prio - b.prio || a.i - b.i);
    return indexed.map((e) => e.step);
}
function installActionHeadline(action) {
    switch (action.kind) {
        case 'deploy_worker': return 'Deploy the Cloudflare Worker.';
        case 'install_plugin': return 'Install the Sill plugin.';
        case 'add_cloudflare': return 'Put the site behind Cloudflare.';
        case 'paste_embed': return 'Paste the Sill embed on your site.';
        case 'add_redirect': return 'Add a redirect to the Sill edge.';
        case 'connect_store': return 'Connect your store.';
        default: return 'Finish your install.';
    }
}
function installActionEvidence(action) {
    switch (action.kind) {
        case 'deploy_worker': return 'The Worker sees every crawler at the network layer and serves your discovery files.';
        case 'install_plugin': return 'The Sill plugin serves your discovery files and the browser embed.';
        case 'add_cloudflare': return action.reason === 'www_not_proxied'
            ? 'The apex is behind Cloudflare but www is not — proxy both to cover every path.'
            : 'A CDN like Cloudflare makes the server-side install one deploy.';
        case 'paste_embed': return 'The Sill script tag identifies agents and logs their visits.';
        case 'add_redirect': return 'A redirect sends the discovery paths to the Sill edge so no deploy is required.';
        case 'connect_store': return `Connect ${action.store} so Sill can authorize orders.`;
        default: return '';
    }
}
/**
 * Pure computation of the readiness verdict from the ordered check list.
 * Deterministic — same input, same output, no clocks, no randomness.
 */
export function readinessVerdict(checks) {
    let counted = 0;
    let passing = 0;
    let has_block_failure = false;
    let has_warn_failure = false;
    for (const c of checks) {
        if (c.uncounted)
            continue;
        if (c.state === 'na' || c.state === 'unknown')
            continue;
        counted += 1;
        if (c.state === 'pass') {
            passing += 1;
            continue;
        }
        if (c.state === 'fail') {
            if (c.severity === 'block')
                has_block_failure = true;
            else
                has_warn_failure = true;
        }
    }
    const kind = has_block_failure
        ? 'not_ready'
        : has_warn_failure
            ? 'almost_ready'
            : 'agent_ready';
    const label = kind === 'agent_ready'
        ? 'Agent-ready'
        : kind === 'almost_ready'
            ? 'Almost ready'
            : 'Not ready yet';
    return { kind, label, passing, counted };
}
//# sourceMappingURL=readiness.js.map